import { useState } from "react";
import { MessageCircle, Minus, Plus, ShieldCheck, ShoppingBag, Zap } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { formatIDR } from "@/data/products";
import { WA_NUMBER } from "@/i18n";
import ShareButtons from "@/components/ShareButtons";

export default function ProductInfo({ product: p }) {
  const { t, lang, addToCart, buyNow, setSelectedProduct } = useStore();
  const [qty, setQty] = useState(1);
  const sold = p.stock === "sold";

  const specs = [
    { label: t.product.size, value: p.specs.size },
    { label: t.product.age, value: `${p.specs.ageMonths} ${t.product.months}` },
    { label: t.product.sex, value: p.specs.sex === "M" ? t.product.male : t.product.female },
    { label: t.product.tail, value: p.strain },
  ];
  const askLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    `${t.product.askMsg} ${p.name} (${formatIDR(p.price)})`,
  )}`;

  return (
    <div className="flex flex-col gap-7" data-testid="product-info">
      <div>
        <div className="mb-4 flex items-center gap-3">
          <span className="h-px w-8 bg-ember" />
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">
            {t.product.eyebrow}
          </span>
        </div>
        <div className="mb-3 flex flex-wrap gap-2">
          <span
            data-testid="product-modal-stock"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${
              sold ? "bg-zinc-800 text-zinc-400" : "bg-emerald-500/15 text-emerald-400"
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${sold ? "bg-zinc-500" : "animate-pulse bg-emerald-400"}`} />
            {sold ? t.catalog.sold : t.catalog.available}
          </span>
          <span className="rounded-full bg-ember px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-black">
            {t.catalog.grade}
          </span>
          <span className="rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-mist">
            {p.category} · {p.strain}
          </span>
        </div>
        <h3 data-testid="product-modal-name" className="font-display text-2xl font-bold leading-tight text-white lg:text-3xl">
          {p.name}
        </h3>
        <p data-testid="product-modal-price" className="mt-3 font-mono text-xl font-bold text-ember-light">
          {formatIDR(p.price)}
        </p>
      </div>

      <div>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t.product.about}</p>
        <p data-testid="product-modal-desc" className="text-sm leading-relaxed text-mist">{p.desc[lang]}</p>
      </div>

      <div>
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t.product.specs}</p>
        <dl className="grid grid-cols-2 gap-2" data-testid="product-modal-specs">
          {specs.map((s) => (
            <div key={s.label} className="rounded-xl border border-white/10 bg-ink-card px-4 py-3">
              <dt className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">{s.label}</dt>
              <dd className="mt-1 text-sm font-semibold text-white">{s.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {sold ? (
        <p
          data-testid="product-modal-sold-note"
          className="rounded-2xl border border-white/10 bg-ink-card px-4 py-3 text-sm text-mist"
        >
          {t.product.soldNote}
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-ink-card px-4 py-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t.product.qty}</span>
        <div className="flex items-center gap-3">
          <button
            data-testid="product-qty-minus"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-ember hover:text-ember"
            aria-label="Decrease"
          >
            <Minus size={13} />
          </button>
          <span data-testid="product-qty-value" className="w-6 text-center font-mono text-sm font-bold text-white">{qty}</span>
          <button
            data-testid="product-qty-plus"
            onClick={() => setQty((q) => q + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-ember hover:text-ember"
            aria-label="Increase"
          >
            <Plus size={13} />
          </button>
        </div>
        <span className="font-mono text-sm font-bold text-ember-light">{formatIDR(p.price * qty)}</span>
      </div>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        <button
          data-testid="product-modal-add-to-cart"
          onClick={() => {
            addToCart(p, qty);
            toast.success(t.toast.added);
            setSelectedProduct(null);
          }}
          className="flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-3.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-ember hover:text-ember"
        >
          <ShoppingBag size={14} />
          {t.catalog.addToCart}
        </button>
        <button
          data-testid="product-modal-buy-now"
          onClick={() => buyNow(p, qty)}
          className="flex items-center justify-center gap-2 rounded-full bg-ember px-5 py-3.5 font-mono text-[11px] font-bold uppercase tracking-widest text-black transition-all duration-300 hover:bg-ember-light hover:shadow-[0_0_28px_rgba(255,107,0,0.4)]"
        >
          <Zap size={14} />
          {t.catalog.buyNow}
        </button>
      </div>
        </>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <ShareButtons product={p} />
        <a
          data-testid="product-modal-ask-wa"
          href={askLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist transition-colors hover:text-ember"
        >
          <MessageCircle size={14} className="text-ember" />
          {t.product.ask}
        </a>
      </div>

      <p className="flex items-center gap-2 border-t border-white/10 pt-5 text-[11px] text-zinc-500">
        <ShieldCheck size={14} className="shrink-0 text-ember" />
        {t.product.guarantee}
      </p>
    </div>
  );
}
